import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import { Printer, FileText } from 'lucide-react';
import TrDateInput from '../components/TrDateInput';

/* ───── Types ───── */
interface Kasa {
  kasaID: number;
  kasaAdi: string;
  kasaTuru: string;
  bakiye: number;
  paraBirimi?: string;
}

interface Hareket {
  hareketID: number;
  tarih: string;
  islemTuru: 'Giris' | 'Cikis';
  miktar: number;
  aciklama: string | null;
  bakiye: number;
  kasa: { kasaID: number; kasaAdi: string; kasaTuru: string } | null;
  cari: { cariID: number; adiSoyadi: string } | null;
  proje: { projeID: number; projeAdi: string } | null;
  odeme: { odemeID: number; odemeTuru: string } | null;
}

interface RaporSonuc {
  hareketler: Hareket[];
  toplamGiris: number;
  toplamCikis: number;
  netBakiye: number;
  devirBakiye?: number;
}

/* ───── Helpers ───── */
const fmtPara = (v: number) =>
  '₺' + v.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const fmtTarih = (s: string) => {
  const d = new Date(s);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};

type DatePreset = 'Bu Hafta' | 'Bu Ay' | 'Bugüne Kadar' | 'Tümü' | 'Özel Tarih';

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'Bu Hafta', label: 'Bu Hafta' },
  { value: 'Bu Ay', label: 'Bu Ay' },
  { value: 'Bugüne Kadar', label: 'Bugüne Kadar' },
  { value: 'Tümü', label: 'Tümü' },
  { value: 'Özel Tarih', label: 'Özel Tarih' },
];

const getDateRange = (
  preset: DatePreset,
  ozelBaslangic: string,
  ozelBitis: string
): { baslangic: string | null; bitis: string | null } => {
  const now = new Date();
  switch (preset) {
    case 'Bu Hafta': {
      const d = new Date(now);
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      d.setDate(d.getDate() + diff);
      return { baslangic: d.toISOString().slice(0, 10), bitis: today() };
    }
    case 'Bu Ay':
      return { baslangic: firstOfMonth(), bitis: today() };
    case 'Bugüne Kadar':
      return { baslangic: null, bitis: today() };
    case 'Tümü':
      return { baslangic: null, bitis: null };
    case 'Özel Tarih':
      return { baslangic: ozelBaslangic, bitis: ozelBitis };
    default:
      return { baslangic: firstOfMonth(), bitis: today() };
  }
};

const ODEME_TURLERI = [
  { value: 'Tumu', label: 'Tümü' },
  { value: 'Nakit', label: 'Nakit' },
  { value: 'Senet', label: 'Senet' },
  { value: 'Cek', label: 'Çek' },
  { value: 'Takas', label: 'Takas' },
];

const KASA_TURU_RENK: Record<string, string> = {
  Nakit: '#16a34a',
  Banka: '#2563eb',
};

/* ═══════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════ */
const HesapHareketleriRaporu: React.FC = () => {
  const [kasalar, setKasalar] = useState<Kasa[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset>('Bu Ay');
  const [ozelBaslangic, setOzelBaslangic] = useState(firstOfMonth());
  const [ozelBitis, setOzelBitis] = useState(today());
  const [secilikKasa, setSecilikKasa] = useState<string>('');
  const [odemeTuru, setOdemeTuru] = useState<string>('Tumu');
  const [sonuc, setSonuc] = useState<RaporSonuc | null>(null);
  const [loading, setLoading] = useState(false);

  /* Kasaları yükle */
  useEffect(() => {
    axiosInstance.get('/api/Kasalar')
      .then(r => setKasalar(r.data))
      .catch(() => toast.error('Kasalar yüklenemedi'));
  }, []);

  /* Raporu getir */
  const fetchRapor = useCallback(async () => {
    setLoading(true);
    try {
      const range = getDateRange(datePreset, ozelBaslangic, ozelBitis);
      const params: Record<string, string> = {};
      if (range.baslangic) params.baslangic = range.baslangic;
      if (range.bitis) params.bitis = range.bitis;
      if (secilikKasa) params.kasaID = secilikKasa;
      if (odemeTuru && odemeTuru !== 'Tumu') params.odemeTuru = odemeTuru;
      const r = await axiosInstance.get('/api/KasaHareketleri/hesap-raporu', { params });
      setSonuc(r.data);
    } catch {
      toast.error('Rapor yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [datePreset, ozelBaslangic, ozelBitis, secilikKasa, odemeTuru]);

  /* İlk yükleme */
  useEffect(() => { fetchRapor(); }, [fetchRapor]);

  const handlePrint = () => window.print();

  const tipLabel = (h: Hareket) => {
    if (h.odeme?.odemeTuru) return h.odeme.odemeTuru.toUpperCase();
    return h.kasa?.kasaTuru?.toUpperCase() || '-';
  };

  const tipRenk = (h: Hareket) => {
    const t = h.odeme?.odemeTuru || h.kasa?.kasaTuru || '';
    if (t === 'Nakit' || t === 'nakit') return '#16a34a';
    if (t === 'Banka' || t === 'banka') return '#2563eb';
    if (t === 'Senet' || t === 'senet') return '#d97706';
    if (t === 'Cek' || t === 'cek') return '#7c3aed';
    return '#6b7280';
  };

  return (
    <div className="hhr-page">
      <style>{`
        .hhr-page {
          padding: 28px 32px;
          background: #f4f5f7;
          min-height: 100vh;
          font-family: 'Inter', sans-serif;
        }
        /* ── Header ── */
        .hhr-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          margin-bottom: 24px;
          gap: 16px;
          flex-wrap: wrap;
        }
        .hhr-title-row { display: flex; align-items: center; gap: 12px; }
        .hhr-icon {
          width: 42px; height: 42px;
          background: #1a2440;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          color: #fff; font-size: 18px; flex-shrink: 0;
        }
        .hhr-title { font-size: 20px; font-weight: 800; color: #1a2440; margin: 0; }
        .hhr-subtitle { font-size: 13px; color: #6b7280; margin: 2px 0 0; }
        .hhr-print-btn {
          display: flex; align-items: center; gap: 6px;
          background: #1a2440; color: #fff; border: none;
          border-radius: 8px; padding: 9px 18px;
          font-size: 13px; font-weight: 600; cursor: pointer;
          transition: background 0.18s;
        }
        .hhr-print-btn:hover { background: #e67d21; }

        /* ── Filter Card ── */
        .hhr-filter-card {
          background: #fff;
          border: 1px solid #e8eaed;
          border-radius: 14px;
          padding: 20px 24px;
          margin-bottom: 22px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .hhr-filter-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr auto;
          gap: 14px;
          align-items: flex-end;
        }
        @media (max-width: 900px) {
          .hhr-filter-grid { grid-template-columns: 1fr 1fr; }
        }
        .hhr-field label {
          display: block;
          font-size: 11px; font-weight: 700;
          color: #6b7280; text-transform: uppercase;
          letter-spacing: 0.06em; margin-bottom: 6px;
        }
        .hhr-field input, .hhr-field select {
          width: 100%; border: 1.5px solid #e2e8f0;
          border-radius: 8px; padding: 8px 12px;
          font-size: 13px; color: #1a1a1a;
          background: #f8fafc; outline: none;
          font-family: 'Inter', sans-serif;
          transition: border-color 0.18s, box-shadow 0.18s;
        }
        .hhr-field input:focus, .hhr-field select:focus {
          border-color: #1a2440;
          box-shadow: 0 0 0 3px rgba(26,36,64,0.10);
          background: #fff;
        }
        .hhr-ara-btn {
          width: 100%; padding: 9px 20px;
          background: #1a2440; color: #fff;
          border: none; border-radius: 8px;
          font-size: 13px; font-weight: 700;
          cursor: pointer; transition: background 0.18s;
          display: flex; align-items: center; justify-content: center; gap: 6px;
          white-space: nowrap;
        }
        .hhr-ara-btn:hover { background: #e67d21; }

        /* ── Summary Cards ── */
        .hhr-summary {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 22px;
        }
        .hhr-sum-card {
          background: linear-gradient(135deg, #1a2440, #243060);
          border-radius: 14px;
          padding: 20px 24px;
          color: #fff;
          box-shadow: 0 4px 16px rgba(26,36,64,0.18);
        }
        .hhr-sum-label {
          font-size: 10px; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.08em;
          color: rgba(255,255,255,0.6); margin-bottom: 8px;
        }
        .hhr-sum-value {
          font-size: 26px; font-weight: 800;
          font-variant-numeric: tabular-nums;
        }
        .hhr-sum-card.giris .hhr-sum-value { color: #6ee7b7; }
        .hhr-sum-card.cikis .hhr-sum-value { color: #fca5a5; }
        .hhr-sum-card.net .hhr-sum-value { color: #e67d21; }

        /* ── Table ── */
        .hhr-table-card {
          background: #fff;
          border: 1px solid #e8eaed;
          border-radius: 14px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
          overflow: hidden;
        }
        .hhr-table-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 24px;
          background: #1a2440;
          border-radius: 14px 14px 0 0;
        }
        .hhr-table-title {
          font-size: 14px; font-weight: 700;
          color: #fff; margin: 0;
        }
        .hhr-table-count {
          font-size: 12px; color: rgba(255,255,255,0.6);
        }
        .hhr-table-wrap { overflow-x: auto; }
        .hhr-table {
          width: 100%; border-collapse: collapse;
        }
        .hhr-table thead tr { background: #f8fafc; }
        .hhr-table thead th {
          padding: 10px 16px;
          font-size: 10px; font-weight: 700;
          color: #6b7280; text-transform: uppercase;
          letter-spacing: 0.07em;
          text-align: left;
          border-bottom: 2px solid #e8eaed;
          white-space: nowrap;
        }
        .hhr-table thead th.num { text-align: right; }
        .hhr-table tbody tr {
          border-bottom: 1px solid #f1f3f4;
          transition: background 0.1s;
        }
        .hhr-table tbody tr:last-child { border-bottom: none; }
        .hhr-table tbody tr:hover { background: #f0f3fa; }
        .hhr-table tbody td {
          padding: 12px 16px;
          font-size: 13px; color: #374151;
          vertical-align: middle;
        }
        .hhr-table tbody td.num {
          text-align: right;
          font-variant-numeric: tabular-nums;
          font-weight: 600;
        }
        .hhr-tip-badge {
          display: inline-block;
          padding: 2px 9px;
          border-radius: 6px;
          font-size: 10px; font-weight: 700;
          letter-spacing: 0.05em;
          color: #fff;
        }
        .hhr-giris { color: #16a34a; font-weight: 700; }
        .hhr-cikis { color: #dc2626; font-weight: 700; }
        .hhr-bakiye { color: #1a2440; font-weight: 700; }
        .hhr-tfoot tr { background: #f8fafc; border-top: 2px solid #e8eaed; }
        .hhr-tfoot td {
          padding: 12px 16px;
          font-size: 13px; font-weight: 700;
          color: #1a2440;
        }
        .hhr-tfoot td.num { text-align: right; }

        /* ── Empty ── */
        .hhr-empty {
          text-align: center; padding: 60px 20px;
          color: #94a3b8;
        }
        .hhr-empty i { font-size: 44px; display: block; margin-bottom: 12px; opacity: 0.4; }

        /* ── Print ── */
        @media print {
          body * { visibility: hidden; }
          .hhr-print-area, .hhr-print-area * { visibility: visible; }
          .hhr-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
          .hhr-header, .hhr-filter-card, .hhr-print-btn { display: none !important; }
          .hhr-summary { page-break-inside: avoid; }
          .hhr-table-card { box-shadow: none !important; border: 1px solid #e2e8f0; }
          .hhr-table-header { background: #1a2440 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>

      {/* ── Header ── */}
      <div className="hhr-header">
        <div className="hhr-title-row">
          <div className="hhr-icon">
            <FileText size={20} />
          </div>
          <div>
            <h1 className="hhr-title">Hesap Hareketleri Raporu</h1>
            <p className="hhr-subtitle">Nakit, banka, çek, senet ve kasa hesaplarının cari ekstreleri</p>
          </div>
        </div>
        {sonuc && (
          <button className="hhr-print-btn" onClick={handlePrint}>
            <Printer size={15} />
            Yazdır
          </button>
        )}
      </div>

      {/* ── Filters ── */}
      <div className="hhr-filter-card">
        <div className="hhr-filter-grid">
          <div className="hhr-field">
            <label>Dönem</label>
            <select value={datePreset} onChange={e => setDatePreset(e.target.value as DatePreset)}>
              {DATE_PRESETS.map(p => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <div className="hhr-field">
            <label>Kasa / Hesap</label>
            <select value={secilikKasa} onChange={e => setSecilikKasa(e.target.value)}>
              <option value="">Tümü</option>
              {kasalar.filter(k => k.kasaTuru === 'Nakit').map(k => (
                <option key={k.kasaID} value={k.kasaID}>
                  {k.kasaAdi}
                </option>
              ))}
            </select>
          </div>
          <div className="hhr-field">
            <label>Ödeme Türü</label>
            <select value={odemeTuru} onChange={e => setOdemeTuru(e.target.value)}>
              {ODEME_TURLERI.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="hhr-field">
            <label>&nbsp;</label>
            <button className="hhr-ara-btn" onClick={fetchRapor} disabled={loading}>
              <i className="fas fa-search" />
              {loading ? 'Yükleniyor...' : 'Ara'}
            </button>
          </div>
        </div>
        {datePreset === 'Özel Tarih' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
            <div className="hhr-field">
              <label>Başlangıç Tarihi</label>
              <TrDateInput value={ozelBaslangic} onChange={e => setOzelBaslangic(e.target.value)} />
            </div>
            <div className="hhr-field">
              <label>Bitiş Tarihi</label>
              <TrDateInput value={ozelBitis} onChange={e => setOzelBitis(e.target.value)} />
            </div>
          </div>
        )}
      </div>

      <div className="hhr-print-area">
        {/* ── Summary ── */}
        {sonuc && (
          <div className="hhr-summary">
            <div className="hhr-sum-card giris">
              <div className="hhr-sum-label">Toplam Giriş (Tahsilat)</div>
              <div className="hhr-sum-value">{fmtPara(sonuc.toplamGiris)}</div>
            </div>
            <div className="hhr-sum-card cikis">
              <div className="hhr-sum-label">Toplam Çıkış (Ödeme)</div>
              <div className="hhr-sum-value">{fmtPara(sonuc.toplamCikis)}</div>
            </div>
            <div className="hhr-sum-card net">
              <div className="hhr-sum-label">Net Bakiye</div>
              <div className="hhr-sum-value">{fmtPara(sonuc.netBakiye)}</div>
              {(sonuc.devirBakiye ?? 0) !== 0 && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>
                  Devir: {fmtPara(sonuc.devirBakiye ?? 0)}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Table ── */}
        <div className="hhr-table-card">
          <div className="hhr-table-header">
            <span className="hhr-table-title">
              <i className="fas fa-table" style={{ marginRight: 8 }} />
              Hesap Hareketleri
            </span>
            <span className="hhr-table-count">
              {sonuc ? `${sonuc.hareketler.length} kayıt` : ''}
            </span>
          </div>
          <div className="hhr-table-wrap">
            {loading ? (
              <div className="hhr-empty">
                <i className="fas fa-spinner fa-spin" />
                <p>Yükleniyor...</p>
              </div>
            ) : !sonuc || sonuc.hareketler.length === 0 ? (
              <div className="hhr-empty">
                <i className="fas fa-inbox" />
                <p>Seçilen kriterlere göre hareket bulunamadı.</p>
              </div>
            ) : (
              <table className="hhr-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Tip</th>
                    <th>Hesap Adı</th>
                    <th>Açıklama</th>
                    <th className="num">Borç (Giriş)</th>
                    <th className="num">Alacak (Çıkış)</th>
                    <th className="num">Bakiye</th>
                  </tr>
                </thead>
                <tbody>
                  {(sonuc.devirBakiye ?? 0) !== 0 && (
                    <tr style={{ background: '#f0f4ff' }}>
                      <td style={{ whiteSpace: 'nowrap', color: '#6b7280', fontStyle: 'italic' }}>
                        {getDateRange(datePreset, ozelBaslangic, ozelBitis).baslangic
                          ? fmtTarih(getDateRange(datePreset, ozelBaslangic, ozelBitis).baslangic!)
                          : '—'}
                      </td>
                      <td>
                        <span className="hhr-tip-badge" style={{ background: '#6b7280' }}>DEVİR</span>
                      </td>
                      <td style={{ fontWeight: 600, color: '#6b7280', fontStyle: 'italic' }}>—</td>
                      <td style={{ color: '#6b7280', fontStyle: 'italic' }}>Önceki dönemden devreden bakiye</td>
                      <td className="num">—</td>
                      <td className="num">—</td>
                      <td className="num">
                        <span style={{ color: (sonuc.devirBakiye ?? 0) >= 0 ? '#1a2440' : '#dc2626', fontWeight: 700 }}>
                          {fmtPara(sonuc.devirBakiye ?? 0)}
                        </span>
                      </td>
                    </tr>
                  )}
                  {sonuc.hareketler.map(h => (
                    <tr key={h.hareketID}>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtTarih(h.tarih)}</td>
                      <td>
                        <span
                          className="hhr-tip-badge"
                          style={{ background: tipRenk(h) }}
                        >
                          {tipLabel(h)}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>{h.kasa?.kasaAdi || '-'}</td>
                      <td style={{ color: '#6b7280' }}>
                        {h.aciklama || (h.cari ? h.cari.adiSoyadi : '-')}
                      </td>
                      <td className="num">
                        {h.islemTuru === 'Giris' ? (
                          <span className="hhr-giris">{fmtPara(h.miktar)}</span>
                        ) : '-'}
                      </td>
                      <td className="num">
                        {h.islemTuru === 'Cikis' ? (
                          <span className="hhr-cikis">{fmtPara(h.miktar)}</span>
                        ) : '-'}
                      </td>
                      <td className="num">
                        <span className="hhr-bakiye">{fmtPara(h.bakiye)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="hhr-tfoot">
                  <tr>
                    <td colSpan={4}><strong>TOPLAM</strong></td>
                    <td className="num">
                      <span className="hhr-giris">{fmtPara(sonuc.toplamGiris)}</span>
                    </td>
                    <td className="num">
                      <span className="hhr-cikis">{fmtPara(sonuc.toplamCikis)}</span>
                    </td>
                    <td className="num">
                      <span className="hhr-bakiye">{fmtPara(sonuc.netBakiye)}</span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HesapHareketleriRaporu;
